import React from 'react';
import pathToRegexp from 'path-to-regexp';
import BigNumber from 'bignumber.js';
import type { History } from 'history';
import Loader from 'components/common/Loader';
import { Notice } from 'components/common/Notice';
import { SalesOverviewAnalytics } from 'common/analytics';
import Wahanda from 'common/wahanda';
import moment from 'common/moment';
import _ from 'common/underscore';
import SALES_REPORTS_ROUTES from '../SalesReportContainer/reportRoutes';
import { CHANNEL } from '../BookingChannelFilter';
import { allEmployeesOptionValue } from '../EmployeeFilter';
import DISPLAY from '../displayStateEnum';
import Filters, { PossibleFilters } from '../common/Filters';
import { ReportTable } from './reportTable';
import type { SalesReportData } from './salesReportTableProps';
import ReportLayout from '../common/ReportLayout';

const toSalesReportPath = pathToRegexp.compile(SALES_REPORTS_ROUTES.SALES_REPORT);

type DisplayState = typeof DISPLAY[keyof typeof DISPLAY];

type SalesReportRouteParams = {
  venueId: string;
  fromDate?: string;
  toDate?: string;
  selectedChannel?: string;
  employeeFilter?: string;
};

type SalesReportQueryParams = {
  venueId: string;
  fromDate: moment.Moment;
  toDate: moment.Moment;
  selectedChannel: string;
  employeeFilter: number;
};

type DatePickerChangeData = {
  fromDate: moment.Moment;
  toDate: moment.Moment;
  type?: string;
};

type SalesReportFilters = PossibleFilters & {
  fromDate: moment.Moment;
  toDate: moment.Moment;
};

export type SalesReportProps = {
  requestSalesReportDataAction: (
    venueId: string,
    from: moment.Moment,
    to: moment.Moment,
    bookingChannel: string | null,
    employeeFilter: number | null,
  ) => void;
  displayState: DisplayState;
  history: Pick<History, 'push'>;
  match: {
    params: SalesReportRouteParams;
  };
  reportData?: SalesReportData | null;
};

export class SalesReport extends React.Component<SalesReportProps> {
  static defaultProps = {
    reportData: null,
  };

  componentDidMount = () => {
    SalesOverviewAnalytics.trackPageView();
    this.requestDataFromApi();
  };

  componentDidUpdate = (prevProps: SalesReportProps) => {
    if (!_.isEqual(this.props.match.params, prevProps.match.params)) {
      this.requestDataFromApi();
    }
  };

  onFilterChange = (filters: SalesReportFilters) => {
    const { venueId } = this.getQueryParams();
    const { fromDate, toDate, selectedChannel, selectedEmployeeId } = filters;
    const newPath = toSalesReportPath({
      venueId,
      fromDate: fromDate.formatApiDateString(),
      toDate: toDate.formatApiDateString(),
      employeeFilter:
        selectedChannel != null && selectedEmployeeId == null
          ? allEmployeesOptionValue
          : selectedEmployeeId,
      selectedChannel,
    });
    this.props.history.push(newPath);
  };

  getQueryParams = (): SalesReportQueryParams => {
    const { venueId, fromDate, toDate, selectedChannel, employeeFilter } = _.defaults(
      this.props.match.params,
      {
        fromDate: moment().startOf('month').formatApiDateString(),
        toDate: moment().endOf('month').formatApiDateString(),
        employeeFilter: allEmployeesOptionValue,
        selectedChannel: CHANNEL.ALL,
      },
    );
    return {
      venueId,
      fromDate: moment(fromDate),
      toDate: moment(toDate),
      selectedChannel,
      employeeFilter: new BigNumber(employeeFilter).toNumber(),
    };
  };

  trackDateFilterChange = (data: DatePickerChangeData) => {
    SalesOverviewAnalytics.trackReportDateFilterChange(data.type);
  };

  requestDataFromApi = () => {
    const { fromDate, toDate, selectedChannel, employeeFilter, venueId } = this.getQueryParams();
    this.props.requestSalesReportDataAction(
      venueId,
      fromDate,
      toDate,
      selectedChannel === CHANNEL.ALL ? null : selectedChannel,
      employeeFilter === allEmployeesOptionValue ? null : employeeFilter,
    );
  };

  render() {
    const queryParams = this.getQueryParams();
    let body: JSX.Element | null = null;
    switch (this.props.displayState) {
      case DISPLAY.WAITING:
        body = <Loader />;
        break;
      case DISPLAY.REPORT: {
        if (!this.props.reportData) {
          body = <Loader />;
          break;
        }
        const {
          venueId,
          fromDate,
          toDate,
          selectedChannel,
          employeeFilter: selectedEmployeeId,
        } = queryParams;
        body = (
          <ReportTable
            venueId={venueId}
            fromDate={fromDate}
            toDate={toDate}
            selectedEmployeeId={selectedEmployeeId}
            selectedChannel={selectedChannel}
            reportData={this.props.reportData}
          />
        );
        break;
      }
      case DISPLAY.ERROR:
        body = <div>{Wahanda.lang.reports.sales.error}</div>;
        break;
      default:
        throw new Error(`Unrecognised display state: ${String(this.props.displayState)}`);
    }
    const filters = (
      <Filters
        date={{
          uniqueId: 'salesReportDateFilter',
          fromDate: queryParams.fromDate,
          toDate: queryParams.toDate,
        }}
        channel={{ selectedChannel: queryParams.selectedChannel }}
        employee={{
          categoryFilterEnabled: true,
          selectedEmployeeId: queryParams.employeeFilter,
        }}
        onFilterChange={this.onFilterChange}
        onDatePickerChange={this.trackDateFilterChange}
        onChannelChange={SalesOverviewAnalytics.trackChannelFilterChange}
        onEmployeeChange={SalesOverviewAnalytics.trackEmployeeFilterChange}
      />
    );
    const messages = (
      <Notice type="success">
        <p
          dangerouslySetInnerHTML={{
            __html: Wahanda.lang.reports.newReportsMessage,
          }}
        />
      </Notice>
    );
    return (
      <ReportLayout
        messages={messages}
        filter={filters}
        showTimeStamp={this.props.displayState === DISPLAY.REPORT}
        body={body}
      />
    );
  }
}
